/**
 * DS-WMS Module Routes Configuration
 *
 * This file contains all route definitions for the Works Management System utility module.
 * Routes are organized by feature for better maintainability.
 */

import { MODULE_HEADER_BG } from '@/theme/moduleHeader';
import React, { lazy, Suspense } from 'react';
import { PageSkeleton } from '@/components/base/Skeleton';
import type { RouteObject } from 'react-router-dom';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import Layout from '@/components/Layout';
import PrivateRoute from '@/components/PrivateRoute';
import { reportsRoutes } from '@/modules/core/reports';
import { HelpProvider, HelpDrawer } from '../help';
import WmsErrorBoundary from '../components/WmsErrorBoundary';
// The UDA scope strip. Mounted as a pathless layout route below, so one instance sits
// above every WMS screen instead of each page hosting its own copy.
import { WmsUdaScopeBar, WmsUdaSelect } from '../components/common/WmsUdaScope';
import { ModuleBannerProvider, type ModuleBanner } from '@/modules/core/layout/moduleBanner';

// Setup and Reports draw the slim module masthead in the WMS blue. Setup carries the
// WMS UDA picker (header tone, which also lifts the strip); the report screens draw the
// picker the report data obeys themselves.
const WMS_BANNER_BG = MODULE_HEADER_BG;
const WMS_SETUP_BANNER: ModuleBanner = { background: WMS_BANNER_BG, title: 'Setup', udaSlot: <WmsUdaSelect tone="header" /> };
const WMS_REPORTS_BANNER: ModuleBanner = { background: WMS_BANNER_BG, title: 'Reports' };

// Wraps the module's Layout with the Help system — drawer is mounted once
// so every WMS page can trigger it via HelpButton / useHelp().
//
// The error boundary is not optional: without it, a throw in any single WMS page
// unmounts the entire admin shell and the user sees a blank white page with no clue
// what broke. Contained here, the sidebar survives and the failure is named.
function WmsShell() {
  return (
    <HelpProvider>
      <WmsErrorBoundary area="This WMS page">
        <Layout />
      </WmsErrorBoundary>
      <HelpDrawer />
    </HelpProvider>
  );
}

function LegacyWmsRedirect() {
  const location = useLocation();
  const nextPath = location.pathname.replace(/^\/ds-wms(?=\/|$)/, '/app/ds-wms');
  return <Navigate to={`${nextPath}${location.search}${location.hash}`} replace />;
}

// WMS Dashboard Pages
const WMSDashboard = lazy(() => import('../pages/dashboard/WMSDashboard'));
const ExecutiveDashboard = lazy(() => import('../pages/dashboard/ExecutiveDashboard'));
const WorksDashboard = lazy(() => import('../pages/dashboard/WorksDashboard'));
const WorksDashboardSummary = lazy(() => import('../pages/dashboard/WorksDashboardSummary'));
const PipelineDashboard = lazy(() => import('../pages/dashboard/PipelineDashboard'));
const FinancialDashboard = lazy(() => import('../pages/dashboard/FinancialDashboard'));
const WMSIntegratedDashboard = lazy(() => import('../pages/dashboard/WMSIntegratedDashboard'));
const PushkaraluDashboard = lazy(() => import('../pages/dashboard/PushkaraluDashboard'));
const FundSchemePage = lazy(() => import('../pages/funds'));
const UlbFundDashboard = lazy(() => import('../pages/funds/ulb-dashboard'));
const UlbYearFinance = lazy(() => import('../pages/funds/ulb-year'));
const BudgetReleasesPage = lazy(() => import('../pages/funds/budget-releases'));

// WMS Transactional Pages - Core
const WorksListPage = lazy(() => import('../pages/works'));
const ProposalsPage = lazy(() => import('../pages/proposals'));
const EstimatesPage = lazy(() => import('../pages/estimates'));
const DesignsPage = lazy(() => import('../pages/designs'));

// Sanctions Pages
const TechnicalSanctionsPage = lazy(() => import('../pages/technical-sanctions'));
// The TS form existed but was never imported or routed, so the stage was
// view-only: no way to create a TS and no way to open one for Submit/Approve.
const TechnicalSanctionFormPage = lazy(() => import('../pages/technical-sanctions/form'));
const AdminSanctionsPage = lazy(() => import('../pages/admin-sanctions'));

// Tendering & Contracts
const TendersPage = lazy(() => import('../pages/tenders'));
const TenderExecutionPage = lazy(() => import('../pages/tender-execution'));
const AwardAgreementPage = lazy(() => import('../pages/award-agreement'));
const ContractsPage = lazy(() => import('../pages/contracts'));

// Workflow Inbox
const WorkflowInboxPage = lazy(() => import('../pages/workflow-inbox'));

// Scheduling & Payments
const WorkSchedulePage = lazy(() => import('../pages/work-schedule'));
const PaymentsPage = lazy(() => import('../pages/payments'));
const BankGuaranteesPage = lazy(() => import('../pages/payments/bank-guarantees'));
const MobilizationAdvancePage = lazy(() => import('../pages/payments/mobilization-advance'));
const PenaltiesDeductionsPage = lazy(() => import('../pages/payments/penalties-deductions'));
const FinalPaymentsPage = lazy(() => import('../pages/payments/final-payments'));
const FundRequestsPage = lazy(() => import('../pages/payments/fund-requests'));
const BudgetEntryPage = lazy(() => import('../pages/payments/budget-entry'));

// Physical Progress Pages
const SachivalayamMappingPage = lazy(() => import('../pages/progress/sachivalayam-mapping'));
const GISMappingPage = lazy(() => import('../pages/planning/gis-mapping'));
const MeasurementsPage = lazy(() => import('../pages/progress/measurements'));
const CheckMeasurementsPage = lazy(() => import('../pages/progress/check-measurements'));
const QCPage = lazy(() => import('../pages/progress/qc'));
const InspectionsPage = lazy(() => import('../pages/progress/inspections'));

// Setup Page
const SetupPage = lazy(() => import('@/modules/core/setup'));

// WMS Masters Pages
const WorkCategoryManagement = lazy(() => import('../masters/work-category'));
const WorkTypeManagement = lazy(() => import('../masters/work-type'));
const WorkStatusManagement = lazy(() => import('../masters/work-status'));
const SchemeManagement = lazy(() => import('../masters/scheme'));
const FinancialYearManagement = lazy(() => import('../masters/financial-year'));
const DepartmentManagement = lazy(() => import('../masters/department'));
const ContractorCategoryManagement = lazy(() => import('../masters/contractor-category'));
const TenderTypeManagement = lazy(() => import('../masters/tender-type'));
const UOMManagement = lazy(() => import('../masters/uom'));
const RateContractTypeManagement = lazy(() => import('../masters/rate-contract-type'));
const SORTypeManagement = lazy(() => import('../masters/sor-type'));
const EstimateTypeManagement = lazy(() => import('../masters/estimate-type'));
const PenaltyTypeManagement = lazy(() => import('../masters/penalty-type'));
const AdvanceTypeManagement = lazy(() => import('../masters/advance-type'));
const BGTypeManagement = lazy(() => import('../masters/bg-type'));
const QCTestTypeManagement = lazy(() => import('../masters/qc-test-type'));
const ContractorStatusManagement = lazy(() => import('../masters/contractor-status'));
const FirmTypeManagement = lazy(() => import('../masters/firm-type'));
const SanctionAuthorityManagement = lazy(() => import('../masters/sanction-authority'));
const PwdClassManagement = lazy(() => import('../masters/pwd-class'));
const FieldDesignationManagement = lazy(() => import('../masters/field-designation'));
const FundingAgencyManagement = lazy(() => import('../masters/funding-agency'));
const FunctionCenterManagement = lazy(() => import('../masters/function-center'));
const BudgetHeadManagement = lazy(() => import('../masters/budget-head'));
const SubSchemeManagement = lazy(() => import('../masters/sub-scheme'));
const SORManagement = lazy(() => import('../masters/sor'));
const StatutoryDeductionManagement = lazy(() => import('../masters/statutory-deduction'));
const NoticeTypeManagement = lazy(() => import('../masters/notice-type'));
const InspectionTypeManagement = lazy(() => import('../masters/inspection-type'));
const AuditTypeManagement = lazy(() => import('../masters/audit-type'));
const TenderDocStructureManagement = lazy(() => import('../masters/tender-doc-structure'));
const MonitoringPanelManagement = lazy(() => import('../masters/monitoring-panel'));
const ProposalSourceManagement = lazy(() => import('../masters/proposal-source'));
const ProjectConfigurationPage = lazy(() => import('../masters/project-configuration'));
const WorkImportorPage = lazy(() => import('../masters/work-importor'));
const ManageAbstractPage = lazy(() => import('../masters/manage-abstract'));
const SubSectorManagement = lazy(() => import('../masters/sub-sector'));

// WMS child routes
export const wmsChildRoutes: RouteObject[] = [
  // Dashboard Routes
  {
    index: true,
    element: <WMSIntegratedDashboard />
  },
  {
    path: 'dashboard',
    element: <WMSDashboard />
  },
  {
    path: 'integrated',
    element: <WMSIntegratedDashboard />
  },
  {
    path: 'executive',
    element: <ExecutiveDashboard />
  },
  {
    path: 'dashboard/works',
    element: <WorksDashboard />
  },
  {
    path: 'works-dashboard',
    element: <WorksDashboardSummary />
  },
  {
    path: 'pipeline',
    element: <PipelineDashboard />
  },
  {
    path: 'dashboard/financial',
    element: <FinancialDashboard />
  },
  {
    path: 'funds',
    element: <FundSchemePage />
  },
  {
    path: 'funds/ulb/:udaId',
    element: <UlbFundDashboard />
  },
  {
    path: 'funds/ulb/:udaId/year/:fy',
    element: <UlbYearFinance />
  },
  {
    path: 'funds/budget-releases',
    element: <BudgetReleasesPage />
  },

  // Workflow Inbox
  {
    path: 'workflow-inbox',
    element: <WorkflowInboxPage />
  },

  // Transactional Routes - Works Lifecycle
  {
    path: 'works',
    element: <WorksListPage />
  },
  {
    path: 'proposals',
    element: <ProposalsPage />
  },
  {
    path: 'estimates',
    element: <EstimatesPage />
  },
  {
    path: 'designs',
    element: <DesignsPage />
  },
  {
    path: 'technical-sanctions',
    element: <TechnicalSanctionsPage />
  },
  {
    // Static segment first so 'new' is never captured by ':id'.
    path: 'technical-sanctions/new',
    element: <TechnicalSanctionFormPage />
  },
  {
    path: 'technical-sanctions/:id',
    element: <TechnicalSanctionFormPage />
  },
  {
    path: 'admin-sanctions',
    element: <AdminSanctionsPage />
  },
  {
    path: 'tenders',
    element: <TendersPage />
  },
  {
    path: 'tender-execution',
    element: <TenderExecutionPage />
  },
  {
    path: 'award-agreement',
    element: <AwardAgreementPage />
  },
  {
    path: 'contracts',
    element: <ContractsPage />
  },
  {
    path: 'work-schedule',
    element: <WorkSchedulePage />
  },
  {
    path: 'payments',
    element: <PaymentsPage />
  },
  {
    path: 'payments/bank-guarantees',
    element: <BankGuaranteesPage />
  },
  {
    path: 'payments/mobilization-advance',
    element: <MobilizationAdvancePage />
  },
  {
    path: 'payments/penalties-deductions',
    element: <PenaltiesDeductionsPage />
  },
  {
    path: 'payments/final-payments',
    element: <FinalPaymentsPage />
  },
  {
    path: 'payments/fund-requests',
    element: <FundRequestsPage />
  },
  {
    path: 'payments/budget-entry',
    element: <BudgetEntryPage />
  },

  // Physical Progress Routes
  {
    path: 'progress/sachivalayam-mapping',
    element: <SachivalayamMappingPage />
  },
  {
    path: 'planning/gis-mapping',
    element: <GISMappingPage />
  },
  {
    path: 'progress/measurements',
    element: <MeasurementsPage />
  },
  {
    path: 'progress/check-measurements',
    element: <CheckMeasurementsPage />
  },
  {
    path: 'progress/qc',
    element: <QCPage />
  },
  {
    path: 'progress/inspections',
    element: <InspectionsPage />
  },

  // Setup Route
  {
    path: 'setup',
    element: <ModuleBannerProvider value={WMS_SETUP_BANNER}><SetupPage /></ModuleBannerProvider>
  },

  // Reports Routes
  {
    path: 'reports',
    element: <ModuleBannerProvider value={WMS_REPORTS_BANNER}><Outlet /></ModuleBannerProvider>,
    children: reportsRoutes
  },

  // Masters Routes
  {
    path: 'masters/work-categories',
    element: <WorkCategoryManagement />
  },
  {
    path: 'masters/work-types',
    element: <WorkTypeManagement />
  },
  {
    path: 'masters/work-statuses',
    element: <WorkStatusManagement />
  },
  {
    path: 'masters/schemes',
    element: <SchemeManagement />
  },
  {
    path: 'masters/financial-years',
    element: <FinancialYearManagement />
  },
  {
    path: 'masters/departments',
    element: <DepartmentManagement />
  },
  /* Work Access Groups is retired — the feature is not in use.
   *
   * Its setup card was hidden in migration 199 (app_stp_opt_lst_t 1000520, a_in =
   * false), and both routes that reached the page are withdrawn here: the current
   * masters/work-access, and masters/department-login-groups, the legacy path that
   * migration 097 pointed at the same workbench. Leaving either registered would keep
   * the page reachable by direct URL, which is what hiding the card was meant to stop.
   *
   * Nothing is deleted: masters/work-access/ and the /ds-wms/assignments/* endpoints
   * behind it are intact, so restoring the feature means restoring these two entries
   * and flipping a_in back on 1000520. */
  {
    path: 'masters/contractor-categories',
    element: <ContractorCategoryManagement />
  },
  {
    path: 'masters/tender-types',
    element: <TenderTypeManagement />
  },
  {
    path: 'masters/uom',
    element: <UOMManagement />
  },
  {
    path: 'masters/rate-contract-types',
    element: <RateContractTypeManagement />
  },
  {
    path: 'masters/sor-types',
    element: <SORTypeManagement />
  },
  {
    path: 'masters/estimate-types',
    element: <EstimateTypeManagement />
  },
  {
    path: 'masters/penalty-types',
    element: <PenaltyTypeManagement />
  },
  {
    path: 'masters/advance-types',
    element: <AdvanceTypeManagement />
  },
  {
    path: 'masters/bg-types',
    element: <BGTypeManagement />
  },
  {
    path: 'masters/qc-test-types',
    element: <QCTestTypeManagement />
  },
  {
    path: 'masters/contractor-statuses',
    element: <ContractorStatusManagement />
  },
  {
    path: 'masters/firm-types',
    element: <FirmTypeManagement />
  },
  {
    path: 'masters/sanction-authorities',
    element: <SanctionAuthorityManagement />
  },
  {
    path: 'masters/pwd-classes',
    element: <PwdClassManagement />
  },
  {
    path: 'masters/designations',
    element: <FieldDesignationManagement />
  },
  {
    path: 'masters/funding-agencies',
    element: <FundingAgencyManagement />
  },
  {
    path: 'masters/function-centers',
    element: <FunctionCenterManagement />
  },
  {
    path: 'masters/budget-heads',
    element: <BudgetHeadManagement />
  },
  {
    path: 'masters/sub-schemes',
    element: <SubSchemeManagement />
  },
  {
    path: 'masters/sor',
    element: <SORManagement />
  },
  {
    path: 'masters/statutory-deductions',
    element: <StatutoryDeductionManagement />
  },
  {
    path: 'masters/notice-types',
    element: <NoticeTypeManagement />
  },
  {
    path: 'masters/inspection-types',
    element: <InspectionTypeManagement />
  },
  {
    path: 'masters/audit-types',
    element: <AuditTypeManagement />
  },
  {
    path: 'masters/tender-doc-structures',
    element: <TenderDocStructureManagement />
  },
  {
    path: 'masters/monitoring-panels',
    element: <MonitoringPanelManagement />
  },
  {
    path: 'masters/proposal-sources',
    element: <ProposalSourceManagement />
  },
  {
    path: 'masters/project-configuration',
    element: <ProjectConfigurationPage />
  },
  {
    path: 'masters/work-importor',
    element: <WorkImportorPage />
  },
  {
    path: 'masters/manage-abstract',
    element: <ManageAbstractPage />
  },
  {
    path: 'masters/sub-sectors',
    element: <SubSectorManagement />
  }
];

// Main WMS route configuration.
//
// wmsChildRoutes sit one level deeper than they read: they hang off a PATHLESS layout
// route whose only job is to render the UDA scope strip above whatever page matched.
// That is what makes the UDA dropdown present on the dashboards, the transaction lists,
// the setup masters and the reports without touching any of them.
export const wmsRoutes: RouteObject = {
  path: '/app/ds-wms',
  element: <PrivateRoute><WmsShell /></PrivateRoute>,
  children: [
    {
      element: <WmsUdaScopeBar />,
      children: wmsChildRoutes
    }
  ]
};

export const wmsLegacyRoutes: RouteObject = {
  path: '/ds-wms/*',
  element: <LegacyWmsRedirect />
};

// Standalone full-screen dashboards — rendered OUTSIDE WmsShell (no sidebar),
// but still auth-guarded. The Godavari Pushkarams 2027 monitoring dashboard is a
// big-screen presentation view for officials, so it takes the whole viewport.
export const wmsFullscreenRoutes: RouteObject = {
  path: '/app/ds-wms/pushkaralu',
  element: (
    <PrivateRoute>
      <Suspense fallback={<div className="p-6"><PageSkeleton /></div>}>
        <PushkaraluDashboard />
      </Suspense>
    </PrivateRoute>
  )
};

export default wmsRoutes;
